var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config";
import { isUsageLimitBody, reportUsageLimit } from "../services/usageLimits";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
export function useExportProgress(sessionId) {
    const { token } = useAuth();
    const [state, setState] = useState({
        isExporting: !!sessionId,
        exportedCount: 0,
        exportTotal: 0,
        error: null,
        isComplete: false,
    });
    const eventSourceRef = useRef(null);
    useEffect(() => {
        if (!(sessionId && token))
            return;
        let cancelled = false;
        const connect = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const ticketRes = yield api.createSearchSessionStreamTicket(token, sessionId);
                if (cancelled || !ticketRes.success)
                    return;
                const url = `${config.API_URL}/search-session/${sessionId}/stream?ticket=${encodeURIComponent(ticketRes.data.ticket)}`;
                const es = new EventSource(url);
                eventSourceRef.current = es;
                es.onmessage = (event) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    try {
                        const data = JSON.parse(event.data);
                        // An RFC export that runs out of daily lookups stops early and says why
                        if (isUsageLimitBody(data.limitReached))
                            reportUsageLimit(data.limitReached);
                        if (data.type === "export-progress") {
                            setState({
                                isExporting: true,
                                exportedCount: (_a = data.exportedCount) !== null && _a !== void 0 ? _a : 0,
                                exportTotal: (_b = data.exportTotal) !== null && _b !== void 0 ? _b : 0,
                                error: null,
                                isComplete: false,
                            });
                        }
                        else if (data.type === "export-complete") {
                            setState({
                                isExporting: false,
                                exportedCount: (_c = data.exportedCount) !== null && _c !== void 0 ? _c : 0,
                                exportTotal: (_d = data.exportedCount) !== null && _d !== void 0 ? _d : 0,
                                error: null,
                                isComplete: true,
                            });
                            es.close();
                            eventSourceRef.current = null;
                        }
                        else if (data.type === "export-cancelled") {
                            setState({
                                isExporting: false,
                                exportedCount: (_e = data.exportedCount) !== null && _e !== void 0 ? _e : 0,
                                exportTotal: (_f = data.exportedCount) !== null && _f !== void 0 ? _f : 0,
                                error: null,
                                isComplete: true,
                            });
                            es.close();
                            eventSourceRef.current = null;
                        }
                        else if (data.type === "export-error") {
                            setState((prev) => {
                                var _a;
                                return (Object.assign(Object.assign({}, prev), { isExporting: false, error: (_a = data.error) !== null && _a !== void 0 ? _a : "Export failed" }));
                            });
                            es.close();
                            eventSourceRef.current = null;
                        }
                        else if (data.type === "export-started") {
                            setState({
                                isExporting: true,
                                exportedCount: 0,
                                exportTotal: (_g = data.limit) !== null && _g !== void 0 ? _g : 0,
                                error: null,
                                isComplete: false,
                            });
                        }
                    }
                    catch (_h) {
                        // Ignore parse errors
                    }
                };
                es.onerror = () => {
                    es.close();
                    eventSourceRef.current = null;
                    if (!cancelled) {
                        setTimeout(() => {
                            if (!cancelled)
                                connect();
                        }, 3000);
                    }
                };
            }
            catch (_a) {
                // Retry on failure
                if (!cancelled) {
                    setTimeout(() => {
                        if (!cancelled)
                            connect();
                    }, 3000);
                }
            }
        });
        connect();
        return () => {
            cancelled = true;
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        };
    }, [sessionId, token]);
    const cancelExport = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!(sessionId && token))
            return;
        try {
            yield api.cancelSessionExport(token, sessionId);
        }
        catch (_a) {
            // Best effort
        }
    }), [sessionId, token]);
    return Object.assign(Object.assign({}, state), { cancelExport });
}
